import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

function useIsMobile() {
  const [mobile, setMobile] = useState(window.innerWidth < 600);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 600);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
}

// Suit definitions
const SUITS = [
  { name: 'Spades',   symbol: '\u2660', color: '#1a1a2e' },
  { name: 'Hearts',   symbol: '\u2665', color: '#dc2626' },
  { name: 'Clubs',    symbol: '\u2663', color: '#1a1a2e' },
  { name: 'Diamonds', symbol: '\u2666', color: '#dc2626' },
];

// Same-color partner suits for Left Bower
const LEFT_BOWER_MAP = {
  Spades: 'Clubs',
  Clubs: 'Spades',
  Hearts: 'Diamonds',
  Diamonds: 'Hearts',
};

function getSuitSymbol(suitName) {
  const s = SUITS.find(x => x.name === suitName);
  return s ? s.symbol : '';
}

function getSuitColor(suitName) {
  const s = SUITS.find(x => x.name === suitName);
  return s ? s.color : '#1a1a2e';
}

// Build the 6 trump cards for a given suit
function getTrumpCards(suitName) {
  const leftSuit = LEFT_BOWER_MAP[suitName];
  return [
    { rank: 'J', suit: suitName,  label: 'Right Bower', border: 'gold' },
    { rank: 'J', suit: leftSuit,  label: 'Left Bower',  border: 'silver' },
    { rank: 'A', suit: suitName,  label: 'Ace',          border: null },
    { rank: 'K', suit: suitName,  label: 'King',         border: null },
    { rank: 'Q', suit: suitName,  label: 'Queen',        border: null },
    { rank: '10', suit: suitName, label: 'Ten',          border: null },
  ];
}

// Mini playing card component
function MiniCard({ rank, suit, label, border, isMobile }) {
  const symbol = getSuitSymbol(suit);
  const color = getSuitColor(suit);
  const w = isMobile ? 64 : 80;
  const h = isMobile ? 92 : 112;

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: w, height: h,
        background: 'linear-gradient(135deg, #fffef5 0%, #f5f0e0 100%)',
        borderRadius: 8,
        border: border ? `3px solid ${border}` : '2px solid #888',
        boxShadow: border === 'gold'
          ? '0 0 12px rgba(255,215,0,0.5)'
          : border === 'silver'
            ? '0 0 10px rgba(192,192,192,0.4)'
            : '0 2px 6px rgba(0,0,0,0.3)',
        display: 'flex', flexDirection: 'column',
        justifyContent: 'space-between', alignItems: 'center',
        padding: isMobile ? '6px 4px' : '8px 6px',
        position: 'relative',
        cursor: 'default',
        fontFamily: "'Georgia', serif",
      }}>
        {/* Top-left rank + suit */}
        <div style={{
          alignSelf: 'flex-start',
          fontSize: isMobile ? 13 : 16, fontWeight: 700,
          color, lineHeight: 1,
        }}>
          <div>{rank}</div>
          <div style={{ fontSize: isMobile ? 11 : 13 }}>{symbol}</div>
        </div>
        {/* Center suit symbol */}
        <div style={{ fontSize: isMobile ? 26 : 32, color, lineHeight: 1 }}>
          {symbol}
        </div>
        {/* Bottom-right rank + suit (upside down) */}
        <div style={{
          alignSelf: 'flex-end',
          fontSize: isMobile ? 13 : 16, fontWeight: 700,
          color, lineHeight: 1,
          transform: 'rotate(180deg)',
        }}>
          <div>{rank}</div>
          <div style={{ fontSize: isMobile ? 11 : 13 }}>{symbol}</div>
        </div>
      </div>
      <div style={{
        marginTop: 6,
        fontSize: isMobile ? 10 : 12,
        color: border === 'gold' ? '#fbbf24' : border === 'silver' ? '#c0c0c0' : '#8b949e',
        fontWeight: border ? 700 : 400,
        fontFamily: "'Georgia', serif",
      }}>
        {label}
      </div>
    </div>
  );
}

// Section card component
function Section({ title, children, isMobile }) {
  return (
    <div style={{
      background: '#161b22',
      borderRadius: 12,
      padding: isMobile ? '18px 16px' : '28px 32px',
      border: '1px solid #30363d',
      marginBottom: isMobile ? 16 : 24,
    }}>
      <h2 style={{
        fontSize: isMobile ? 18 : 22,
        fontWeight: 700,
        color: '#58a6ff',
        marginTop: 0,
        marginBottom: isMobile ? 10 : 14,
        fontFamily: "'Georgia', serif",
      }}>
        {title}
      </h2>
      <div style={{
        color: '#c9d1d9',
        lineHeight: 1.75,
        fontSize: isMobile ? 14 : 16,
        fontFamily: "'Georgia', serif",
      }}>
        {children}
      </div>
    </div>
  );
}

export default function TutorialPage() {
  const isMobile = useIsMobile();
  const [selectedSuit, setSelectedSuit] = useState('Spades');
  const trumpCards = getTrumpCards(selectedSuit);

  return (
    <>
    <SEO
      title="How to Play Buck Euchre - Tutorial"
      description="Learn how to play Buck Euchre with our interactive tutorial. Master trump calls, card play strategy, and scoring rules."
      path="/tutorial"
      jsonLd={{
        "@context": "https://schema.org",
        "@type": "HowTo",
        "name": "How to Play Buck Euchre",
        "description": "Learn the rules and strategy of Buck Euchre, a popular trick-taking card game.",
        "step": [
          {"@type": "HowToStep", "name": "Deal", "text": "Deal 5 cards to each player from a 24-card deck (9 through Ace)."},
          {"@type": "HowToStep", "name": "Turn Up Trump", "text": "Flip the top remaining card face-up. Players decide whether to accept this suit as trump."},
          {"@type": "HowToStep", "name": "Call Trump", "text": "If all players pass, a second round allows calling any suit as trump."},
          {"@type": "HowToStep", "name": "Play Tricks", "text": "Play 5 tricks. Follow suit if possible. Highest trump or highest card of led suit wins each trick."},
          {"@type": "HowToStep", "name": "Score", "text": "Win 3+ tricks to score points. Fail and you go negative (get 'bucked')."}
        ]
      }}
    />
    <div style={{ fontFamily: "'Georgia', serif", color: '#e2e8f0' }}>
      {/* Page title */}
      <h1 style={{
        fontSize: isMobile ? 24 : 36,
        fontWeight: 700,
        textAlign: 'center',
        marginTop: 0,
        marginBottom: isMobile ? 16 : 24,
        fontFamily: "'Georgia', serif",
      }}>
        How to Play Buck Euchre
      </h1>

      {/* Suit selector buttons */}
      <div style={{
        display: 'flex', justifyContent: 'center',
        gap: isMobile ? 12 : 20,
        marginBottom: isMobile ? 12 : 20,
        flexWrap: 'wrap',
      }}>
        {SUITS.map(s => {
          const isActive = selectedSuit === s.name;
          return (
            <button
              key={s.name}
              onClick={() => setSelectedSuit(s.name)}
              style={{
                width: isMobile ? 64 : 80,
                height: isMobile ? 64 : 80,
                borderRadius: 12,
                border: isActive ? '3px solid #58a6ff' : '2px solid #30363d',
                background: isActive
                  ? (s.color === '#1a1a2e' ? 'rgba(88,166,255,0.25)' : 'rgba(88,166,255,0.15)')
                  : (s.color === '#1a1a2e' ? '#2d333b' : '#161b22'),
                cursor: 'pointer',
                display: 'flex', flexDirection: 'column',
                justifyContent: 'center', alignItems: 'center',
                transition: 'all 0.2s ease',
                boxShadow: isActive ? '0 0 16px rgba(88,166,255,0.3)' : '0 2px 8px rgba(0,0,0,0.3)',
              }}
            >
              <span style={{ fontSize: isMobile ? 30 : 40, color: s.color === '#1a1a2e' ? '#c9d1d9' : s.color, lineHeight: 1 }}>
                {s.symbol}
              </span>
              <span style={{
                fontSize: isMobile ? 9 : 11, marginTop: 4,
                color: isActive ? '#58a6ff' : '#8b949e',
                fontWeight: isActive ? 700 : 400,
                fontFamily: "'Georgia', serif",
              }}>
                {s.name}
              </span>
            </button>
          );
        })}
      </div>

      {/* Trump cards display */}
      <div style={{
        background: '#161b22',
        borderRadius: 12,
        padding: isMobile ? '16px 10px' : '24px 28px',
        border: '1px solid #30363d',
        marginBottom: isMobile ? 20 : 32,
      }}>
        <div style={{
          textAlign: 'center',
          marginBottom: isMobile ? 10 : 16,
          fontSize: isMobile ? 14 : 17,
          color: '#8b949e',
          fontFamily: "'Georgia', serif",
        }}>
          Trump cards when <span style={{ color: getSuitColor(selectedSuit), fontWeight: 700 }}>
            {getSuitSymbol(selectedSuit)} {selectedSuit}
          </span> is trump (highest to lowest):
        </div>
        <div style={{
          display: 'flex', justifyContent: 'center',
          gap: isMobile ? 8 : 14,
          flexWrap: 'wrap',
        }}>
          {trumpCards.map((card, i) => (
            <MiniCard
              key={i}
              rank={card.rank}
              suit={card.suit}
              label={card.label}
              border={card.border}
              isMobile={isMobile}
            />
          ))}
        </div>
      </div>

      {/* Rules sections */}
      <Section title="Choosing the Dealer" isMobile={isMobile}>
        <p style={{ marginTop: 0 }}>
          To pick the first dealer, deal one card face-up to each player going around the table.
          The first player to get a Jack becomes the dealer for the first hand. After that, the
          deal passes to the left after each hand.
        </p>
      </Section>

      <Section title="Dealing" isMobile={isMobile}>
        <p style={{ marginTop: 0 }}>
          In a <strong>3-player game</strong>, Buck Euchre uses a 20-card deck (10, Jack, Queen, King, Ace in
          each suit). In a <strong>4-player game</strong>, the 9s are added for a 24-card deck (9, 10, Jack,
          Queen, King, Ace in each suit).
        </p>
        <p>
          The dealer gives the cards out in two passes — first 3 cards to each player, then 2 cards
          to each player, for 5 cards total. After dealing, the dealer turns one card face-up on
          top of the remaining deck. That turned-up card proposes the trump suit for the hand.
        </p>
      </Section>

      <Section title="Calling Trump - Round 1" isMobile={isMobile}>
        <p style={{ marginTop: 0 }}>
          Starting with the player to the dealer's left and going around the table, each player
          can either pass or tell the dealer to "pick it up." If someone says pick it up, the
          turned-up card's suit becomes trump. In this version of the game, the turned-up card
          stays on the table as a reminder of what trump is — everyone has already seen it.
          That's it for Round 1.
        </p>
        <p>
          If everyone passes, including the dealer, the turned-up card is flipped face-down and
          we move on to Round 2.
        </p>
      </Section>

      <Section title="Calling Trump - Round 2" isMobile={isMobile}>
        <p style={{ marginTop: 0 }}>
          Now the same players go around again, but this time anyone can name a <em>different</em> suit
          as trump (you can't pick the suit that was just turned down). If someone names a suit,
          that becomes trump and they're the caller.
        </p>
        <p>
          <strong style={{ color: '#fbbf24' }}>The "Next" Strategy:</strong> The suit that's the same color
          as the turned-down card is called "next." For example, if a Heart was turned down,
          Diamonds is "next." Many experienced players like to call next because there's a good
          chance the other players are short in that color family — after all, nobody wanted the
          first suit! It's not a guarantee, but it's a solid percentage play.
        </p>
      </Section>

      <Section title="Spades as First Trump" isMobile={isMobile}>
        <p style={{ marginTop: 0 }}>
          <strong style={{ color: '#fbbf24' }}>House Rule:</strong> In this version of Buck Euchre,
          when a Spade is turned up, it is <strong>automatically trump</strong> — no one gets a choice.
          Everyone must play the hand with Spades as trump. This makes the game more unpredictable
          and leaves a large part up to chance, which is part of the fun.
        </p>
        <p>
          When Spades are auto-trump, the dealer must win at least 3 tricks or get penalized,
          while every other player must win at least 1 trick. There is no option to sit out
          on a Spades hand.
        </p>
      </Section>

      <Section title="Playing or Sitting Out" isMobile={isMobile}>
        <p style={{ marginTop: 0 }}>
          This is what makes Buck Euchre special! Once trump is called, every player (except the
          one who called trump) gets to decide: <strong>play</strong> or <strong>sit out</strong>.
          The exception is Spades hands — when Spades is turned up, it's automatically trump and
          everyone must play.
        </p>
        <p>
          The player who called trump <em>must</em> play — they started this, so they're in.
          Everyone else looks at their hand and makes a choice. If you think you can win at
          least one trick, you'll want to play. If your hand is weak in trump and you're worried
          about getting euchred (taking zero tricks), sitting out is the safe move.
        </p>
        <p>
          Why does this matter? Because if you play and take <strong>zero tricks</strong>, you get
          hit with a 5-point penalty. Ouch. Sitting out means you don't score anything that hand,
          but you also don't risk that penalty.
        </p>
      </Section>

      <Section title="Playing Tricks" isMobile={isMobile}>
        <p style={{ marginTop: 0 }}>
          The player to the dealer's left leads the first trick by playing any card. Going
          clockwise, each player must <strong>follow suit</strong> if they can. If you can't
          follow suit, you can play any card — including trump to steal the trick.
        </p>
        <p>
          The highest card of the led suit wins the trick, unless someone played trump, in which
          case the highest trump wins. The trick winner leads the next one. Remember: the
          Right Bower (Jack of trump) is the highest card, followed by the Left Bower (Jack of
          the same-color suit), then Ace, King, Queen, 10 of trump. Use the card display above
          to see the trump card hierarchy for each suit!
        </p>
        <p>
          <strong>Note:</strong> The Left Bower counts as a trump card, not as a card of its
          printed suit. So if Spades are trump, the Jack of Clubs is actually a Spade for that
          hand!
        </p>
      </Section>

      <Section title="Scoring" isMobile={isMobile}>
        <p style={{ marginTop: 0 }}>
          Everyone starts at <strong>21 points</strong> and the goal is to get down to
          exactly <strong>0</strong> (or below). Each trick you win is worth 1 point off your score.
          So win 3 tricks, subtract 3 from your total.
        </p>
        <p>
          But here's the catch: if you chose to <strong>play</strong> and take <strong>zero
          tricks</strong>, you get <strong>5 points added</strong> to your score — you just
          went the wrong direction! That's called getting <strong>"euchred,"</strong> and it's
          the core tension of the game. Every hand, you're weighing the risk: play and try to
          score, or sit out and stay safe?
        </p>
        <p>
          Players who sit out are not affected — no penalty, but no progress toward zero either.
        </p>
      </Section>

      <Section title="Winning & Losing Bucks" isMobile={isMobile}>
        <p style={{ marginTop: 0 }}>
          In Buck Euchre, a "buck" is the wager unit. Before the game starts, players agree on
          a rate — for example, $1 per game and 10 cents ($0.10) per point.
        </p>
        <p>
          The game ends as soon as <strong>any player reaches 0</strong> (or below). That player
          wins! Everyone else looks at how many points they have left, multiplies that by the
          agreed-upon per-point rate, and pays up. Then it's on to the next game.
        </p>
        <p>
          This is why every decision in Buck Euchre matters — calling trump, playing or sitting —
          it all adds up to real stakes. The faster you get to zero, the less you owe.
        </p>
      </Section>

      <nav style={{
        borderTop: '1px solid #30363d',
        marginTop: isMobile ? 16 : 24,
        paddingTop: 16,
      }}>
        <h3 style={{
          fontSize: isMobile ? 14 : 16,
          fontWeight: 600,
          color: '#8b949e',
          marginBottom: 10,
          fontFamily: "'Georgia', serif",
        }}>
          Learn More
        </h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 24px' }}>
          <Link to="/buck-euchre-rules" style={{ color: '#58a6ff', textDecoration: 'none', fontSize: isMobile ? 13 : 15, fontFamily: "'Georgia', serif" }}>Complete Buck Euchre Rules</Link>
          <Link to="/buck-euchre-strategy" style={{ color: '#58a6ff', textDecoration: 'none', fontSize: isMobile ? 13 : 15, fontFamily: "'Georgia', serif" }}>Strategy Guide</Link>
          <Link to="/euchre-variations" style={{ color: '#58a6ff', textDecoration: 'none', fontSize: isMobile ? 13 : 15, fontFamily: "'Georgia', serif" }}>Euchre Variations</Link>
        </div>
      </nav>
    </div>
    </>
  );
}
